/* ═══════════════════════════════════════════════════
   MEMBER ACTIVITY — "Where You Left Off"

   Shopify keeps no per-customer viewing history, so this records it in the
   member's own browser, the same way scheme-detail.js already stores
   favourites. Written by the pages a member opens; read by the Dashboard.

   Consequences worth remembering:
     • History is per-browser. The same member on their phone has a separate
       list. That is the accepted trade-off for having no backend.
     • Keyed by customer id, so two people sharing a computer don't see each
       other's history. Logged-out visitors share a "guest" bucket.
     • Every localStorage call can throw (private windows, blocked storage),
       so all of them are guarded and simply degrade to "no history".
   ═══════════════════════════════════════════════════ */

window.FilmStudyActivity = (function () {
  var MAX_ENTRIES = 12;
  var PREFIX = 'filmstudy_activity_';

  function storageKey(customerId) {
    var id = customerId == null ? '' : String(customerId).trim();
    return PREFIX + (id || 'guest');
  }

  function read(customerId) {
    try {
      var raw = localStorage.getItem(storageKey(customerId));
      var parsed = raw ? JSON.parse(raw) : [];
      return Object.prototype.toString.call(parsed) === '[object Array]' ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  function write(customerId, entries) {
    try {
      localStorage.setItem(storageKey(customerId), JSON.stringify(entries));
    } catch (err) {
      /* Quota or blocked storage — history just won't persist. */
    }
  }

  /* Record an open. Re-opening something moves it to the front and refreshes
     its timestamp rather than adding a duplicate. */
  function record(customerId, item) {
    if (!item || !item.id) return;

    var entry = {
      id: String(item.id),
      title: item.title || '',
      url: item.url || '',
      image: item.image || '',
      game: item.game || '',
      kind: item.kind || 'scheme',
      level: item.level || '',
      ts: Date.now()
    };

    if (!entry.title || !entry.url) return;

    var kept = read(customerId).filter(function (existing) {
      return existing && existing.id !== entry.id;
    });

    kept.unshift(entry);
    write(customerId, kept.slice(0, MAX_ENTRIES));
  }

  /* Most-recent-first, newest at index 0. Entries missing the fields a card
     needs are dropped rather than rendered half-empty. */
  function list(customerId, limit) {
    var entries = read(customerId).filter(function (entry) {
      return entry && entry.title && entry.url;
    });
    entries.sort(function (a, b) {
      return (b.ts || 0) - (a.ts || 0);
    });
    return entries.slice(0, limit || MAX_ENTRIES);
  }

  function clear(customerId) {
    try {
      localStorage.removeItem(storageKey(customerId));
    } catch (err) {
      /* nothing to do */
    }
  }

  /* ── Seen / watched ──────────────────────────────────

     Deliberately a SEPARATE store from the recent-activity list above.

     That list is capped at 12 because it feeds a three-card shelf — it answers
     "what was I last on". This answers "have I ever opened this", across a
     library of 40+ tips, so a cap of 12 would quietly forget things a member
     had watched and re-mark them unread. Different question, different store.

     Shape is { id: timestamp } rather than a plain list so the mark can be
     aged or sorted later without a migration. Capped generously, dropping the
     oldest first, purely so localStorage can't grow without limit.
     ─────────────────────────────────────────────────── */

  var SEEN_PREFIX = 'filmstudy_seen_';
  var MAX_SEEN = 400;

  function seenKey(customerId) {
    var id = customerId == null ? '' : String(customerId).trim();
    return SEEN_PREFIX + (id || 'guest');
  }

  function seenMap(customerId) {
    try {
      var raw = localStorage.getItem(seenKey(customerId));
      var parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' && !(parsed instanceof Array) ? parsed : {};
    } catch (err) {
      return {};
    }
  }

  function hasSeen(customerId, id) {
    if (!id) return false;
    return Object.prototype.hasOwnProperty.call(seenMap(customerId), String(id));
  }

  function markSeen(customerId, id) {
    if (!id) return;
    try {
      var map = seenMap(customerId);
      map[String(id)] = Date.now();

      var keys = Object.keys(map);
      if (keys.length > MAX_SEEN) {
        keys.sort(function (a, b) {
          return (map[a] || 0) - (map[b] || 0);
        });
        keys.slice(0, keys.length - MAX_SEEN).forEach(function (k) {
          delete map[k];
        });
      }

      localStorage.setItem(seenKey(customerId), JSON.stringify(map));
    } catch (err) {
      /* Blocked or full storage — marks just won't persist. */
    }
  }

  function clearSeen(customerId) {
    try {
      localStorage.removeItem(seenKey(customerId));
    } catch (err) {
      /* nothing to do */
    }
  }

  /* "2 days ago" / "16h ago" — short enough for a card, no library. */
  function timeAgo(timestamp) {
    if (!timestamp) return '';
    var seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'Just now';
    var minutes = Math.floor(seconds / 60);
    if (minutes < 60) return minutes + (minutes === 1 ? ' min ago' : ' mins ago');
    var hours = Math.floor(minutes / 60);
    if (hours < 24) return hours + 'h ago';
    var days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return days + ' days ago';
    var weeks = Math.floor(days / 7);
    if (weeks < 5) return weeks === 1 ? 'A week ago' : weeks + ' weeks ago';
    var months = Math.floor(days / 30);
    return months <= 1 ? 'A month ago' : months + ' months ago';
  }

  return {
    record: record,
    list: list,
    clear: clear,
    timeAgo: timeAgo,
    storageKey: storageKey,
    markSeen: markSeen,
    hasSeen: hasSeen,
    seenMap: seenMap,
    clearSeen: clearSeen,
    seenKey: seenKey
  };
})();
